import { ArrowRight, Award, BookOpen, CheckCircle2, ClipboardList, Eye, LockKeyhole, PlayCircle } from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { Badge } from "../components/Badge";
import { EmptyState } from "../components/EmptyState";
import { Shell } from "../components/Shell";
import { StatCard } from "../components/StatCard";
import type {
  Assessment,
  ModuleContent,
  ProgressSummary,
  Question,
  ResultAttempt,
  ResultResponse,
  SessionUser,
  TrainingModule
} from "../types";
import { readStoredTab } from "../utils/navigation";

type StudentPageProps = {
  user: SessionUser;
  onLogout: () => void;
};

const nav = [
  { id: "home", label: "Inicio", icon: "dashboard" as const },
  { id: "modules", label: "Meus modulos", icon: "modules" as const },
  { id: "results", label: "Resultados", icon: "results" as const }
];

const STUDENT_ACTIVE_TAB_KEY = "setup_student_active_tab";
const studentNavIds = nav.map((item) => item.id);

export function StudentPage({ user, onLogout }: StudentPageProps) {
  const [active, setActive] = useState(() => readStoredTab(STUDENT_ACTIVE_TAB_KEY, "home", studentNavIds));
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [progress, setProgress] = useState<ProgressSummary | null>(null);
  const [results, setResults] = useState<ResultAttempt[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [contents, setContents] = useState<ModuleContent[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const [submittingAssessment, setSubmittingAssessment] = useState(false);

  const selectedModule = modules.find((module) => module.id_modulo === selectedModuleId);
  const selectedAssessment = assessments.find((assessment) => assessment.id_avaliacao === selectedAssessmentId);

  const loadHome = useCallback(async () => {
    const [moduleRows, progressData, resultRows] = await Promise.all([api.modules(), api.progress(), api.results()]);
    setModules(moduleRows);
    setProgress(progressData);
    setResults(resultRows);
    setSelectedModuleId((current) => current || (moduleRows[0]?.id_modulo ?? ""));
    setNotice("");
  }, []);

  const loadModule = useCallback(async (moduleId: string) => {
    if (!moduleId) return { contentRows: [], assessmentRows: [] };
    const [contentRows, assessmentRows] = await Promise.all([api.contents(moduleId), api.assessments(moduleId)]);
    return { contentRows, assessmentRows };
  }, []);

  useEffect(() => {
    loadHome().catch((error) => setNotice(error.message));
  }, [loadHome]);

  useEffect(() => {
    window.localStorage.setItem(STUDENT_ACTIVE_TAB_KEY, active);
  }, [active]);

  useEffect(() => {
    if (!notice) return;
    const timeout = window.setTimeout(() => setNotice(""), 4500);
    return () => window.clearTimeout(timeout);
  }, [notice]);

  useEffect(() => {
    let ignore = false;
    setContents([]);
    setAssessments([]);
    setSelectedAssessmentId("");
    setQuestions([]);
    setAnswers({});

    if (!selectedModuleId) return;

    loadModule(selectedModuleId)
      .then(({ contentRows, assessmentRows }) => {
        if (ignore) return;
        const moduleContents = contentRows.filter((content) => content.id_modulo === selectedModuleId);
        const moduleAssessments = assessmentRows.filter((assessment) => assessment.id_modulo === selectedModuleId);
        setContents(moduleContents);
        setAssessments(moduleAssessments);
        setSelectedAssessmentId(moduleAssessments[0]?.id_avaliacao ?? "");
        setNotice("");
      })
      .catch((error) => {
        if (ignore) return;
        setContents([]);
        setAssessments([]);
        setSelectedAssessmentId("");
        setNotice(error instanceof Error ? error.message : "Nao foi possivel carregar o modulo.");
      });

    return () => {
      ignore = true;
    };
  }, [selectedModuleId, loadModule]);

  useEffect(() => {
    if (questions.length === 0) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [questions.length]);

  async function refresh(message?: string) {
    const moduleId = selectedModuleId;
    await loadHome();
    if (moduleId) {
      const { contentRows, assessmentRows } = await loadModule(moduleId);
      const moduleContents = contentRows.filter((content) => content.id_modulo === moduleId);
      const moduleAssessments = assessmentRows.filter((assessment) => assessment.id_modulo === moduleId);
      setContents(moduleContents);
      setAssessments(moduleAssessments);
      setSelectedAssessmentId(moduleAssessments[0]?.id_avaliacao ?? "");
    }
    if (message) setNotice(message);
  }

  async function openAssessment(assessmentId: string) {
    try {
      setSelectedAssessmentId(assessmentId);
      setQuestions(await api.questions(assessmentId));
      setNotice("");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Nao foi possivel abrir a avaliacao.");
    }
  }

  async function submitAssessment(event: FormEvent) {
    event.preventDefault();
    if (!selectedAssessmentId || submittingAssessment) return;
    setSubmittingAssessment(true);
    try {
      const payload = Object.entries(answers).map(([id_pergunta, resposta_usuario]) => ({
        id_pergunta,
        resposta_usuario
      }));
      const result = await api.submitAssessment(selectedAssessmentId, payload);
      const resultLabel =
        result.status_correcao === "pending_manual"
          ? `Avaliacao enviada. Nota parcial: ${result.percentual ?? 0}%. Aguardando correcao da discursiva.`
          : `Avaliacao enviada. Resultado: ${result.percentual ?? "aguardando correcao"}%.`;
      await refresh(resultLabel);
      setQuestions([]);
      setAnswers({});
    } catch (error) {
      const message = error instanceof Error ? error.message : "Nao foi possivel enviar a avaliacao.";
      if (message.includes("ja foi realizada")) {
        try {
          await refresh(message);
        } catch (refreshError) {
          setNotice(refreshError instanceof Error ? refreshError.message : message);
        }
        setQuestions([]);
        setAnswers({});
      } else {
        setNotice(message);
      }
    } finally {
      setSubmittingAssessment(false);
    }
  }

  const pendingAssessments = useMemo(
    () => assessments.filter((assessment) => assessment.status === "published"),
    [assessments]
  );
  const hasPublishedContent = contents.length > 0;
  const allContentCompleted = hasPublishedContent && contents.every((content) => content.concluido);
  const missingCostCenter = user.centros_custo_permitidos.length === 0;
  const activityLabel =
    active === "modules" && selectedModule?.titulo
      ? selectedModule.titulo
      : active === "results"
        ? "Resultados"
        : "Inicio";

  return (
    <Shell
      user={user}
      items={nav}
      active={active}
      onNavigate={setActive}
      onLogout={onLogout}
      activityLabel={activityLabel}
      activityModuleId={active === "modules" ? selectedModule?.id_modulo : undefined}
    >
      {missingCostCenter ? (
        <div className="notice">
          Seu cadastro esta ativo, mas ainda nao possui Centro de Custo definido. Os modulos serao liberados pelo
          administrador.
        </div>
      ) : null}
      {notice ? <div className="notice">{notice}</div> : null}
      {active === "home" && (
        <section className="page-grid">
          <div className="section-heading">
            <div>
              <span>Seu aprendizado</span>
              <h1>Inicio</h1>
            </div>
          </div>
          <div className="stats-grid">
            <StatCard label="Progresso geral" value={`${progress?.progresso_geral ?? 0}%`} icon={<Award size={20} />} />
            <StatCard label="Modulos liberados" value={modules.length} icon={<BookOpen size={20} />} />
            <StatCard
              label="Conteudos concluidos"
              value={progress?.conteudos_concluidos ?? 0}
              icon={<CheckCircle2 size={20} />}
            />
            <StatCard label="Avaliacoes feitas" value={progress?.avaliacoes_realizadas ?? 0} icon={<ClipboardList size={20} />} />
          </div>
          <ModuleCards
            modules={modules}
            onOpen={(id) => {
              setSelectedModuleId(id);
              setActive("modules");
            }}
          />
        </section>
      )}

      {active === "modules" && (
        <section className="page-grid">
          <div className="section-heading">
            <div>
              <span>Conteudos liberados</span>
              <h1>Meus modulos</h1>
            </div>
          </div>
          <ModuleCards modules={modules} selectedId={selectedModuleId} onOpen={setSelectedModuleId} />
          <div className="student-module-layout">
            <div className="panel">
              {!selectedModule ? (
                <EmptyState title="Nenhum modulo selecionado" text="Escolha um card para estudar." />
              ) : (
                <>
                  <div className="detail-title">
                    <div>
                      <h2>{selectedModule.titulo}</h2>
                      <p>{selectedModule.descricao}</p>
                    </div>
                    <Badge tone="blue">{selectedModule.progresso_percentual ?? 0}%</Badge>
                  </div>
                  {contents.length === 0 ? (
                    <EmptyState
                      title="Sem conteudos publicados"
                      text="Os conteudos deste modulo aparecem aqui quando estiverem com status Publicado."
                    />
                  ) : (
                    <div className="content-timeline">
                      {contents.map((content, index) => {
                        const unlocked =
                          content.liberado ?? Boolean(content.concluido || contents.slice(0, index).every((item) => item.concluido));

                        return (
                          <article className={`content-item ${unlocked ? "" : "locked"}`} key={content.id_conteudo}>
                            <div className="content-check">
                              {content.concluido ? (
                                <CheckCircle2 size={18} />
                              ) : unlocked ? (
                                <PlayCircle size={18} />
                              ) : (
                                <LockKeyhole size={18} />
                              )}
                            </div>
                            <div className="content-body">
                              <div className="content-header">
                                <strong>{content.titulo}</strong>
                                {content.obrigatorio ? <Badge tone="amber">Obrigatorio</Badge> : <Badge>Opcional</Badge>}
                              </div>
                              {unlocked ? (
                                <ContentRenderer content={content} />
                              ) : (
                                <div className="locked-content">
                                  <LockKeyhole size={18} />
                                  Conclua o conteudo anterior para liberar este item.
                                </div>
                              )}
                              <button
                                className="secondary-button"
                                type="button"
                                disabled={content.concluido || !unlocked}
                                onClick={async () => {
                                  try {
                                    await api.completeContent(content.id_modulo, content.id_conteudo);
                                    await refresh("Conteudo marcado como concluido.");
                                  } catch (error) {
                                    setNotice(error instanceof Error ? error.message : "Nao foi possivel concluir o conteudo.");
                                  }
                                }}
                              >
                                <CheckCircle2 size={18} />
                                {content.concluido ? "Concluido" : unlocked ? "Marcar como concluido" : "Bloqueado"}
                              </button>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  )}
                  <div className="module-assessments">
                    <h2>Avaliacoes disponiveis</h2>
                    {pendingAssessments.length === 0 ? (
                      <EmptyState
                        title="Sem avaliacao publicada"
                        text="A avaliacao aparece para o aluno quando estiver com status Publicado."
                      />
                    ) : (
                      <div className="list-stack">
                        {pendingAssessments.map((assessment) => {
                          const attempt = results.find((result) => result.id_avaliacao === assessment.id_avaliacao);
                          const alreadySubmitted = Boolean(attempt);

                          return (
                            <button
                              className={`module-row ${selectedAssessmentId === assessment.id_avaliacao ? "selected" : ""}`}
                              type="button"
                              key={assessment.id_avaliacao}
                              disabled={!allContentCompleted || alreadySubmitted}
                              onClick={() => {
                                if (alreadySubmitted) {
                                  setNotice("Esta avaliacao ja foi realizada.");
                                  return;
                                }
                                if (!allContentCompleted) {
                                  setNotice(
                                    hasPublishedContent
                                      ? "Conclua todos os conteudos do modulo para liberar a avaliacao."
                                      : "A avaliacao sera liberada quando houver conteudo publicado no modulo."
                                  );
                                  return;
                                }
                                openAssessment(assessment.id_avaliacao);
                              }}
                            >
                              <div>
                                <strong>{assessment.titulo}</strong>
                                <span>{assessmentStatusText(assessment, attempt, allContentCompleted)}</span>
                              </div>
                              {alreadySubmitted ? <CheckCircle2 size={18} /> : <ArrowRight size={18} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {active === "results" && (
        <ResultsSection results={results} />
      )}
      {questions.length > 0 ? (
        <div className="assessment-overlay" role="dialog" aria-modal="true" aria-label="Avaliacao em andamento">
          <form className="assessment-shell" onSubmit={submitAssessment}>
            <header className="assessment-header">
              <div>
                <span>Avaliacao em andamento</span>
                <h1>{selectedAssessment?.titulo ?? "Avaliacao"}</h1>
              </div>
              <Badge tone="blue">{questions.length} pergunta(s)</Badge>
            </header>
            <div className="assessment-question-list">
              {questions.map((question) => (
                <QuestionField
                  key={question.id_pergunta}
                  question={question}
                  value={answers[question.id_pergunta] ?? ""}
                  onChange={(value) => setAnswers({ ...answers, [question.id_pergunta]: value })}
                />
              ))}
            </div>
            <div className="assessment-submit-bar">
              <button className="primary-button" type="submit" disabled={submittingAssessment}>
                {submittingAssessment ? "Enviando..." : "Enviar avaliacao"}
                <ArrowRight size={18} />
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </Shell>
  );
}

function ModuleCards({
  modules,
  selectedId,
  onOpen
}: {
  modules: TrainingModule[];
  selectedId?: string;
  onOpen: (id: string) => void;
}) {
  if (modules.length === 0) {
    return <EmptyState title="Nenhum modulo liberado" text="Quando houver treinamento para seu Centro de Custo, ele aparece aqui." />;
  }

  return (
    <div className="module-card-grid">
      {modules.map((module) => (
        <button
          key={module.id_modulo}
          type="button"
          className={`training-card ${selectedId === module.id_modulo ? "selected" : ""}`}
          onClick={() => onOpen(module.id_modulo)}
        >
          <div className="card-ribbon" />
          <h2>{module.titulo}</h2>
          <p>{module.descricao}</p>
          <div className="progress-track">
            <span style={{ width: `${module.progresso_percentual ?? 0}%` }} />
          </div>
          <small>{module.progresso_percentual ?? 0}% concluido</small>
        </button>
      ))}
    </div>
  );
}

function ResultsSection({ results }: { results: ResultAttempt[] }) {
  const [selectedAttemptId, setSelectedAttemptId] = useState("");
  const [responses, setResponses] = useState<ResultResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedResult = results.find((result) => result.id_tentativa === selectedAttemptId);

  useEffect(() => {
    if (!selectedAttemptId || results.some((result) => result.id_tentativa === selectedAttemptId)) return;
    setSelectedAttemptId("");
    setResponses([]);
    setError("");
  }, [results, selectedAttemptId]);

  async function openAnswerKey(result: ResultAttempt) {
    if (!canViewAnswerKey(result)) {
      setSelectedAttemptId(result.id_tentativa);
      setResponses([]);
      setError("O gabarito sera liberado apos a correcao da questao discursiva.");
      return;
    }

    setSelectedAttemptId(result.id_tentativa);
    setResponses([]);
    setError("");
    setLoading(true);
    try {
      setResponses(await api.responses(result.id_tentativa));
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Nao foi possivel carregar o gabarito.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="page-grid">
      <div className="section-heading">
        <div>
          <span>Notas liberadas</span>
          <h1>Meus resultados</h1>
        </div>
      </div>
      <div className="panel table-wrap">
        {results.length === 0 ? (
          <EmptyState title="Nenhum resultado liberado" text="Suas avaliacoes concluidas aparecerao aqui." />
        ) : (
          <table>
            <thead>
              <tr>
                <th>Modulo</th>
                <th>Avaliacao</th>
                <th>Nota</th>
                <th>Status</th>
                <th>Data</th>
                <th>Gabarito</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => {
                const available = canViewAnswerKey(result);
                return (
                  <tr key={result.id_tentativa}>
                    <td>{result.modulo_titulo}</td>
                    <td>{result.avaliacao_titulo}</td>
                    <td>
                      {result.status_correcao === "pending_manual" ? "Parcial " : ""}
                      {result.percentual ?? "-"}%
                    </td>
                    <td>{correctionLabel(result.status_correcao)}</td>
                    <td>{formatDate(result.data_envio)}</td>
                    <td>
                      <button
                        className="secondary-button answer-key-button"
                        type="button"
                        disabled={!available || loading}
                        title={available ? "Ver gabarito" : "Liberado apos a correcao discursiva"}
                        onClick={() => openAnswerKey(result)}
                      >
                        <Eye size={16} />
                        {available ? "Ver gabarito" : "Pendente"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {selectedAttemptId ? (
        <div className="panel answer-key-panel">
          <div className="answer-key-header">
            <div>
              <span>Gabarito</span>
              <h2>{selectedResult?.avaliacao_titulo ?? "Avaliacao"}</h2>
            </div>
            {selectedResult ? <Badge tone={canViewAnswerKey(selectedResult) ? "green" : "amber"}>{correctionLabel(selectedResult.status_correcao)}</Badge> : null}
          </div>
          {loading ? <div className="file-preview-status">Carregando gabarito...</div> : null}
          {error ? <div className="form-error">{error}</div> : null}
          {!loading && !error && responses.length === 0 ? (
            <EmptyState title="Sem respostas" text="Nao ha respostas registradas para esta tentativa." />
          ) : null}
          {!loading && !error && responses.length > 0 ? (
            <div className="answer-key-list">
              {responses.map((response) => (
                <AnswerKeyCard key={response.id_resposta} response={response} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function AnswerKeyCard({ response }: { response: ResultResponse }) {
  const question = response.pergunta;
  const feedback = response.feedback || question?.feedback || "";
  const score = Number(response.pontuacao_obtida || 0);
  const maxScore = question?.pontos ?? Number(response.pontuacao_obtida || 0);
  const showCorrectAnswer = Boolean(question?.resposta_correta);

  return (
    <article className={`answer-key-card ${answerStatusClass(response.correta)}`}>
      <div className="answer-card-header">
        <div>
          <strong>{question?.enunciado ?? "Pergunta removida"}</strong>
          <span>{formatQuestionType(question?.tipo)}</span>
        </div>
        <Badge tone={answerStatusTone(response.correta)}>{answerStatusLabel(response.correta)}</Badge>
      </div>
      {question?.url_imagem ? <img className="question-image" src={question.url_imagem} alt="" /> : null}
      <div className="answer-key-grid">
        <div>
          <span>Sua resposta</span>
          <strong>{formatAnswerValue(response.resposta_usuario, question?.tipo)}</strong>
        </div>
        {showCorrectAnswer ? (
          <div>
            <span>{question?.tipo === "short_answer" ? "Resposta esperada" : "Resposta correta"}</span>
            <strong>{formatAnswerValue(question?.resposta_correta, question?.tipo)}</strong>
          </div>
        ) : (
          <div>
            <span>Correcao</span>
            <strong>{question?.tipo === "essay" ? "Discursiva avaliada" : "Resposta nao disponivel"}</strong>
          </div>
        )}
        <div>
          <span>Nota</span>
          <strong>
            {score} / {maxScore}
          </strong>
        </div>
      </div>
      {feedback ? (
        <div className="answer-feedback">
          <strong>Feedback</strong>
          <p>{feedback}</p>
        </div>
      ) : null}
    </article>
  );
}

type FilePreview = {
  fileName: string;
  mimeType: string;
  objectUrl?: string;
  text?: string;
  html?: string;
};

function ContentRenderer({ content }: { content: ModuleContent }) {
  const [preview, setPreview] = useState<FilePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState("");

  useEffect(() => {
    setPreview(null);
    setPreviewError("");
  }, [content.id_conteudo]);

  useEffect(() => {
    return () => {
      if (preview?.objectUrl) URL.revokeObjectURL(preview.objectUrl);
    };
  }, [preview?.objectUrl]);

  async function openPreview() {
    if (!content.id_arquivo_drive) return;

    setLoadingPreview(true);
    setPreviewError("");
    try {
      if (isLegacyDocFile(content.tipo_arquivo, content.nome_arquivo || content.url_arquivo)) {
        const file = await api.previewFile(content.id_arquivo_drive);
        setPreview((current) => {
          if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
          return {
            fileName: content.nome_arquivo || file.name,
            mimeType: file.mimeType,
            text: file.text
          };
        });
        return;
      }

      const file = await api.downloadFile(content.id_arquivo_drive);
      const objectUrl = URL.createObjectURL(file.blob);
      const nextPreview: FilePreview = {
        fileName: content.nome_arquivo || file.fileName,
        mimeType: file.mimeType,
        objectUrl
      };

      if (isDocxFile(file.mimeType, nextPreview.fileName)) {
        const { default: mammoth } = await import("mammoth");
        const result = await mammoth.convertToHtml({ arrayBuffer: await file.blob.arrayBuffer() });
        nextPreview.html = result.value;
      } else if (isTextFile(file.mimeType, nextPreview.fileName)) {
        nextPreview.text = await file.blob.text();
      }

      setPreview((current) => {
        if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl);
        return nextPreview;
      });
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : "Nao foi possivel visualizar o arquivo.");
    } finally {
      setLoadingPreview(false);
    }
  }

  return (
    <div className="content-text">
      {content.texto ? <p>{content.texto}</p> : null}
      {content.id_arquivo_drive ? (
        <button className="file-preview-trigger" type="button" onClick={openPreview} disabled={loadingPreview}>
          {content.nome_arquivo || content.url_arquivo}
        </button>
      ) : null}
      {!content.id_arquivo_drive && content.url_arquivo ? (
        <div className="file-preview-panel">
          <iframe className="file-preview-frame" src={content.url_arquivo} title={content.titulo} />
        </div>
      ) : null}
      {loadingPreview ? <div className="file-preview-status">Carregando visualizacao...</div> : null}
      {previewError ? <div className="form-error">{previewError}</div> : null}
      {preview ? <FilePreviewPanel preview={preview} /> : null}
    </div>
  );
}

function FilePreviewPanel({ preview }: { preview: FilePreview }) {
  return (
    <div className="file-preview-panel">
      <div className="file-preview-header">
        <strong>{preview.fileName}</strong>
        <span>{preview.mimeType || fileExtension(preview.fileName).toUpperCase()}</span>
      </div>
      {renderFilePreview(preview)}
    </div>
  );
}

function renderFilePreview(preview: FilePreview) {
  if (preview.mimeType.startsWith("image/")) {
    return <img className="content-media" src={preview.objectUrl} alt={preview.fileName} />;
  }

  if (preview.mimeType.startsWith("video/")) {
    return <video className="content-media" src={preview.objectUrl} controls />;
  }

  if (preview.mimeType === "application/pdf" || fileExtension(preview.fileName) === "pdf") {
    return <iframe className="file-preview-frame" src={preview.objectUrl} title={preview.fileName} />;
  }

  if (preview.html !== undefined) {
    return <div className="docx-preview" dangerouslySetInnerHTML={{ __html: preview.html }} />;
  }

  if (preview.text !== undefined) {
    return <pre className="text-preview">{preview.text}</pre>;
  }

  return (
    <div className="locked-content">
      Este formato nao possui visualizacao direta no navegador. O arquivo foi carregado com autenticacao na plataforma.
    </div>
  );
}

function isDocxFile(mimeType: string, fileName: string): boolean {
  return (
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    fileExtension(fileName) === "docx"
  );
}

function isLegacyDocFile(mimeType: string, fileName: string): boolean {
  return mimeType === "application/msword" || fileExtension(fileName) === "doc";
}

function isTextFile(mimeType: string, fileName: string): boolean {
  const extension = fileExtension(fileName);
  return mimeType.startsWith("text/") || ["csv", "json", "md", "txt"].includes(extension);
}

function fileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() ?? "";
}

function QuestionField({
  question,
  value,
  onChange
}: {
  question: Question;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <fieldset className="question-box">
      <legend>
        {question.enunciado}
        <span>{question.pontos} ponto(s)</span>
      </legend>
      {question.url_imagem ? <img className="question-image" src={question.url_imagem} alt="" /> : null}
      {question.tipo === "multiple_choice" ? (
        question.alternativas.map((option) => (
          <label className="check-row" key={option}>
            <input
              type="radio"
              name={question.id_pergunta}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {option}
          </label>
        ))
      ) : question.tipo === "true_false" ? (
        ["true", "false"].map((option) => (
          <label className="check-row" key={option}>
            <input
              type="radio"
              name={question.id_pergunta}
              checked={value === option}
              onChange={() => onChange(option)}
            />
            {option === "true" ? "Verdadeiro" : "Falso"}
          </label>
        ))
      ) : question.tipo === "essay" ? (
        <textarea value={value} onChange={(event) => onChange(event.target.value)} required />
      ) : (
        <input value={value} onChange={(event) => onChange(event.target.value)} required />
      )}
    </fieldset>
  );
}

function formatDate(value?: string) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(new Date(value));
}

function correctionLabel(status: string) {
  const labels: Record<string, string> = {
    auto_graded: "Corrigida automaticamente",
    pending_manual: "Aguardando discursiva",
    manually_graded: "Corrigida pelo admin"
  };
  return labels[status] ?? status;
}

function canViewAnswerKey(result: ResultAttempt) {
  return result.status_correcao !== "pending_manual";
}

function answerStatusLabel(status: string) {
  const labels: Record<string, string> = {
    true: "Correta",
    false: "Incorreta",
    pending: "Pendente"
  };
  return labels[status] ?? status;
}

function answerStatusTone(status: string): "green" | "amber" | "red" | "blue" | "neutral" {
  if (status === "true") return "green";
  if (status === "pending") return "amber";
  if (status === "false") return "red";
  return "neutral";
}

function answerStatusClass(status: string) {
  if (status === "true") return "answer-correct";
  if (status === "pending") return "answer-pending";
  if (status === "false") return "answer-wrong";
  return "";
}

function formatQuestionType(type?: Question["tipo"]) {
  const labels: Record<Question["tipo"], string> = {
    multiple_choice: "Multipla escolha",
    true_false: "Verdadeiro ou falso",
    short_answer: "Resposta curta",
    essay: "Discursiva"
  };
  return type ? labels[type] : "Pergunta";
}

function formatAnswerValue(value?: string, type?: Question["tipo"]) {
  const text = String(value ?? "").trim();
  if (!text) return "-";
  if (type === "true_false") {
    if (text === "true") return "Verdadeiro";
    if (text === "false") return "Falso";
  }
  if (text.startsWith("[") && text.endsWith("]")) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed.map((item) => String(item)).join(", ");
    } catch {
      return text;
    }
  }
  return text;
}

function assessmentStatusText(
  assessment: Assessment,
  attempt: ResultAttempt | undefined,
  allContentCompleted: boolean
) {
  if (attempt?.status_correcao === "pending_manual") {
    return `Realizada - nota parcial ${attempt.percentual ?? 0}%`;
  }
  if (attempt) return `Realizada - resultado ${attempt.percentual ?? 0}%`;
  return allContentCompleted ? `Nota minima ${assessment.nota_minima}` : "Bloqueada ate concluir os conteudos";
}
